import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Check, TrendingUp, Award, Package } from 'lucide-react'
import { useDashboardEmbedded } from '@/hooks/useDashboardEmbedded'

import type { BookingAllowanceState } from '@/types'

interface TierInfo {
  cumulative_purchases: number
  current_tier: {
    min_bookings: number
    max_bookings: number
    price_per_booking: number
  }
  next_tier: {
    min_bookings: number
    max_bookings: number
    price_per_booking: number
  } | null
  bookings_to_next_tier: number
  remaining_bookings: number
  allowance?: BookingAllowanceState | null
}

interface Tier {
  min_bookings: number
  max_bookings: string | number
  base_price_per_booking: number
  base_price_per_booking_formatted: string
  your_price_per_booking: number
  your_price_per_booking_formatted: string
  plan_discount_applied: number
  plan_name: string
  is_current: boolean
  volume_discount_percent: number
}

interface Addon {
  id: string
  name: string
  description: string
  bookings_included: number
  price: number
  price_formatted: string
  tier_minimum: number
}

interface MyAddon {
  id: string
  bookings_purchased: number
  bookings_remaining: number
  bookings_used: number
  status: string
  addon_name: string
  created_at: string
  expires_at: string | null
}

async function fetchTierInfo(): Promise<TierInfo> {
  const response = await fetch('/api/v1/user/booking-addons/tier-info', {
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
    },
  })
  if (!response.ok) throw new Error('Failed to fetch tier info')
  const data = await response.json()
  return data.data
}

async function fetchTiers(): Promise<{ tiers: Tier[]; plan_discounts: Record<string, number> }> {
  const response = await fetch('/api/v1/user/booking-addons/tiers', {
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
    },
  })
  if (!response.ok) throw new Error('Failed to fetch tiers')
  const data = await response.json()
  return data.data
}

async function fetchAvailableAddons(): Promise<{ addons: Addon[]; tier_info: TierInfo }> {
  const response = await fetch('/api/v1/user/booking-addons/available', {
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
    },
  })
  if (!response.ok) throw new Error('Failed to fetch addons')
  const data = await response.json()
  return data.data
}

async function fetchMyAddons(): Promise<{ purchases: MyAddon[]; summary: any }> {
  const response = await fetch('/api/v1/user/booking-addons/my-addons', {
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
    },
  })
  if (!response.ok) throw new Error('Failed to fetch my addons')
  const data = await response.json()
  return data.data
}

async function calculatePrice(bookings: number): Promise<any> {
  const response = await fetch('/api/v1/user/booking-addons/calculate', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
    },
    body: JSON.stringify({ bookings }),
  })
  if (!response.ok) throw new Error('Failed to calculate price')
  const data = await response.json()
  return data.data
}

export default function BookingAddonsPage() {
  const embedded = useDashboardEmbedded()
  const [customBookings, setCustomBookings] = useState(10)
  const [selectedAddon, setSelectedAddon] = useState<Addon | null>(null)
  const [calculatedPrice, setCalculatedPrice] = useState<any>(null)

  const { data: tierInfo, isLoading: tierInfoLoading } = useQuery({
    queryKey: ['booking-addon-tier-info'],
    queryFn: fetchTierInfo,
  })

  const { data: tiers, isLoading: tiersLoading } = useQuery({
    queryKey: ['booking-addon-tiers'],
    queryFn: fetchTiers,
  })

  const { data: availableData, isLoading: addonsLoading } = useQuery({
    queryKey: ['booking-addon-available'],
    queryFn: fetchAvailableAddons,
  })

  const { data: myAddonsData } = useQuery({
    queryKey: ['booking-addon-my-addons'],
    queryFn: fetchMyAddons,
  })

  const handleCustomCalculation = async () => {
    if (customBookings > 0) {
      const price = await calculatePrice(customBookings)
      setCalculatedPrice(price)
    }
  }

  const handleAddonSelection = async (addon: Addon) => {
    setSelectedAddon(addon)
    const price = await calculatePrice(addon.bookings_included)
    setCalculatedPrice(price)
  }

  const handlePurchase = async () => {
    const bookings = selectedAddon?.bookings_included || customBookings
    if (!bookings || bookings < 10) {
      alert('Please select an addon or enter at least 10 bookings')
      return
    }

    try {
      const response = await fetch('/api/v1/user/booking-addons/checkout', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          bookings,
          success_url: `${window.location.origin}/dashboard/billing/booking-packs?purchased=1`,
          cancel_url: `${window.location.origin}/dashboard/billing/booking-packs`,
        }),
      })
      const data = await response.json()
      if (!response.ok || !data?.data?.checkout_url) {
        throw new Error(data?.message || 'Checkout failed')
      }
      window.location.href = data.data.checkout_url
    } catch (err: any) {
      alert(err.message || 'Unable to start checkout')
    }
  }

  if (tierInfoLoading || tiersLoading || addonsLoading) {
    return <div className="p-6">Loading...</div>
  }

  const addons = availableData?.addons || []
  const myPurchases = myAddonsData?.purchases || []
  const allowance = tierInfo?.allowance

  return (
    <div className="p-6 space-y-6">
      {!embedded && (
        <div>
          <h1 className="text-2xl font-bold mb-2">Booking Addons</h1>
          <p className="text-muted-foreground">
            Purchase extra bookings on demand. Addons are always active until exhausted.
          </p>
        </div>
      )}

      {allowance && !allowance.unlimited && (
        <Card className={allowance.is_depleted || allowance.in_overdraft ? 'border-amber-400' : ''}>
          <CardHeader>
            <CardTitle>Booking allowance</CardTitle>
            <CardDescription>
              Plan quota, add-ons, and overdraft for this billing period
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            {allowance.carryover_debt > 0 && (
              <p className="text-amber-700">
                Carryover debt from last period: {allowance.carryover_debt} bookings
              </p>
            )}
            {allowance.is_depleted && (
              <p className="text-amber-700 font-medium">
                Allowance empty — {allowance.quota_hold_count} customer request(s) queued
              </p>
            )}
            {allowance.in_overdraft && !allowance.is_depleted && (
              <p className="text-amber-700">
                In overdraft: {allowance.overdraft_used} / {allowance.overdraft_limit} used
              </p>
            )}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-muted-foreground">Plan used</p>
                <p className="text-lg font-semibold">{allowance.used} / {allowance.effective_plan}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Add-on balance</p>
                <p className="text-lg font-semibold">{allowance.addon_remaining}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Overdraft</p>
                <p className="text-lg font-semibold">{allowance.overdraft_used} / {allowance.overdraft_limit}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Balance</p>
                <p className="text-lg font-semibold">{allowance.balance}</p>
              </div>
            </div>
            <p className="text-muted-foreground">
              Cancellation full refunds remaining: {Math.max(0, allowance.cancellation_full_refund_budget - allowance.cancellation_refunds_used)}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Current Status */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Your Tier
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <Award className="w-5 h-5 text-amber-500" />
              <span className="text-2xl font-bold">
                €{tierInfo?.current_tier.price_per_booking.toFixed(3)} / booking
              </span>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              {tierInfo?.cumulative_purchases} total bookings purchased
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Remaining Addon Bookings
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <Package className="w-5 h-5 text-blue-500" />
              <span className="text-2xl font-bold">{tierInfo?.remaining_bookings || 0}</span>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              From purchased addon packs
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Progress to Next Tier
            </CardTitle>
          </CardHeader>
          <CardContent>
            {tierInfo?.next_tier ? (
              <>
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-green-500" />
                  <span className="text-lg font-bold">
                    {tierInfo.bookings_to_next_tier} more bookings
                  </span>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  to unlock €{tierInfo.next_tier.price_per_booking.toFixed(3)} / booking
                </p>
              </>
            ) : (
              <p className="text-sm text-green-600 font-medium">Maximum tier reached!</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Tier Pricing Table */}
      <Card>
        <CardHeader>
          <CardTitle>Your Personalized Pricing</CardTitle>
          <CardDescription>
            Volume discounts based on total bookings purchased + plan discount based on your subscription
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {tiers?.tiers?.map((tier, index) => (
              <div
                key={index}
                className={`p-4 rounded-lg border ${
                  tier.is_current ? 'border-green-500 bg-green-50' : 'border-gray-200'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium">
                    {tier.min_bookings === 0 ? 'Base Tier' : `${tier.min_bookings}+ bookings`}
                  </span>
                  {tier.is_current && (
                    <span className="text-xs bg-green-500 text-white px-2 py-1 rounded">
                      Current
                    </span>
                  )}
                </div>
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="text-2xl font-bold">{tier.your_price_per_booking_formatted}</span>
                  {tier.plan_discount_applied > 0 && (
                    <span className="text-sm text-green-600">(+{tier.plan_discount_applied}% {tier.plan_name} discount)</span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">per booking</p>
                <div className="mt-2 pt-2 border-t">
                  <p className="text-xs text-muted-foreground">
                    Base price: {tier.base_price_per_booking_formatted}
                    {tier.volume_discount_percent > 0 && (
                      <span className="text-green-600"> ({tier.volume_discount_percent}% volume off)</span>
                    )}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Available Addon Packs */}
      <Card>
        <CardHeader>
          <CardTitle>Purchase Additional Bookings</CardTitle>
          <CardDescription>
            Select a pack or enter a custom amount. Addons are active until exhausted.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {addons.slice(0, 6).map((addon) => (
              <div
                key={addon.id}
                className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                  selectedAddon?.id === addon.id
                    ? 'border-primary bg-primary/5'
                    : 'border-gray-200 hover:border-primary/50'
                }`}
                onClick={() => handleAddonSelection(addon)}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium">{addon.name}</span>
                  <Check
                    className={`w-5 h-5 ${
                      selectedAddon?.id === addon.id ? 'text-primary' : 'text-transparent'
                    }`}
                  />
                </div>
                <p className="text-sm text-muted-foreground mb-2">{addon.description}</p>
                <div className="flex items-center justify-between">
                  <span className="text-lg font-bold">€{(addon.price / 100).toFixed(2)}</span>
                  <span className="text-sm text-muted-foreground">
                    €{(addon.price / addon.bookings_included / 100).toFixed(3)} / booking
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Custom Amount */}
          <div className="border-t pt-6">
            <h4 className="font-medium mb-3">Or enter custom amount</h4>
            <div className="flex gap-4 items-end">
              <div className="w-48">
                <Input
                  type="number"
                  min="10"
                  max="1000"
                  value={customBookings}
                  onChange={(e) => {
                    setCustomBookings(parseInt(e.target.value) || 0)
                    setSelectedAddon(null)
                    setCalculatedPrice(null)
                  }}
                  placeholder="Number of bookings (min 10)"
                />
              </div>
              <Button variant="outline" onClick={handleCustomCalculation}>
                Calculate Price
              </Button>
            </div>
          </div>

          {/* Price Calculation */}
          {calculatedPrice && (
            <div className="mt-6 p-4 bg-muted rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-muted-foreground">Bookings</span>
                <span className="font-medium">{calculatedPrice.bookings}</span>
              </div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-muted-foreground">Price per booking</span>
                <span className="font-medium">
                  €{calculatedPrice.price_per_booking.toFixed(3)}
                </span>
              </div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-muted-foreground">After purchase, you'll have</span>
                <span className="font-medium">
                  {calculatedPrice.cumulative_after} cumulative bookings
                </span>
              </div>
              <div className="flex items-center justify-between pt-2 border-t">
                <span className="text-lg font-bold">Total</span>
                <span className="text-lg font-bold">€{calculatedPrice.total_price_formatted}</span>
              </div>
              <Button className="w-full mt-4" onClick={handlePurchase}>
                Purchase Addon Pack
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* My Active Addon Packs */}
      {myPurchases.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>My Active Addon Packs</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {myPurchases.map((purchase) => (
                <div
                  key={purchase.id}
                  className="flex items-center justify-between p-4 border rounded-lg"
                >
                  <div>
                    <p className="font-medium">{purchase.addon_name}</p>
                    <p className="text-sm text-muted-foreground">
                      Purchased {new Date(purchase.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold">{purchase.bookings_remaining}</p>
                    <p className="text-sm text-muted-foreground">bookings remaining</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
